import { useCallback } from 'react';
import { useStore } from '@nanostores/react';
import { useQueryClient } from '@tanstack/react-query';
import type { AccountsVerificationMessage } from '@customerio/hydra-ui-api';
import { hydraUiApi, useHydraUIAPIQuery } from '@/services/api';
import { accountStore } from '@/services/auth/store';
import { verificationReplyMark } from './verification-session-state';
import { VerificationReviewState, VerificationState } from '@/services/auth/types';
import { useIsAccountVerified, useVerificationNeedsAction } from './account-verification';

const FIVE_MINUTES_MS = 5 * 60 * 1000;

export interface VerificationMessageState {
  /** Null for not-in-review, nothing stored, and a failed fetch alike — the step treats all three the same. */
  message: AccountsVerificationMessage | null;
  /**
   * When the account last answered, or null if it has not. Combines the stored
   * record with a local mark, because the record lags: the reply only lands
   * once Zendesk reports it and Trusty syncs, which is seconds to minutes.
   */
  repliedAt: Date | null;
  isLoading: boolean;
  /** Call once a reply is actually submitted, to acknowledge it before the record catches up. */
  markReplied: () => void;
}

/**
 * The outstanding ask, chosen by `sent_at` rather than by position. The endpoint
 * sorts oldest-first, but showing a stale ask while hiding the current one is
 * the exact failure this feature exists to prevent, so don't stake it on an
 * ordering contract the client can't check. Unparseable timestamps keep the
 * earlier candidate rather than winning the comparison.
 */
function latestBySentAt<T extends { sent_at: string }>(items: readonly T[]): T | null {
  return items.reduce<T | null>((latest, candidate) => {
    if (latest == null) return candidate;
    const candidateSentAt = Date.parse(candidate.sent_at);
    const latestSentAt = Date.parse(latest.sent_at);
    if (Number.isNaN(candidateSentAt)) return latest;
    if (Number.isNaN(latestSentAt)) return candidate;
    return candidateSentAt >= latestSentAt ? candidate : latest;
  }, null);
}

/** The later of two instants, ignoring nulls. */
function laterOf(a: Date | null, b: Date | null): Date | null {
  if (a == null) return b;
  if (b == null) return a;
  return a.getTime() >= b.getTime() ? a : b;
}

/** A parsed instant, or null when absent or unparseable. */
function asDate(value: string | undefined): Date | null {
  if (value == null) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Whether a reply answers the ask on screen, rather than an earlier one. An
 * unreadable ask date imposes no constraint — better to acknowledge a reply we
 * know about than to hide it over a bad timestamp.
 */
function answersAsk(replyAt: Date | null, askedAt: Date | null): boolean {
  if (replyAt == null) return false;
  if (askedAt == null) return true;
  return replyAt.getTime() > askedAt.getTime();
}

/**
 * The "we need more information" email services sent this account.
 *
 * Only fetched in `needs_more_info` — the only transition that produces one, so
 * any other state would be a guaranteed-empty request on every render.
 */
export function useVerificationMessage(): VerificationMessageState {
  const account = useStore(accountStore);
  const queryClient = useQueryClient();
  const markedReplyAt = useStore(verificationReplyMark);
  // The store rather than useAuth: this hook is now read from the rail and the
  // banner as well as the step, and useAuth throws where the stores are unset.
  const accountId = account?.id;
  const needsMoreInfo = account?.verification_review_state === VerificationReviewState.NeedsMoreInfo;

  const { data, isPending } = useHydraUIAPIQuery({
    queryKey: ['getVerificationMessages', { accountId }],
    queryFn: ({ signal }) =>
      hydraUiApi.accounts.getVerificationMessages({
        params: { path: { account_id: String(accountId) } },
        signal,
      }),
    queryConfig: { enabled: needsMoreInfo && accountId != null, staleTime: FIVE_MINUTES_MS },
  });

  const markReplied = useCallback(() => {
    verificationReplyMark.set(new Date());
    // The record is authoritative once it catches up; ask for it rather than
    // leaving the local mark to stand in indefinitely.
    void queryClient.invalidateQueries({ queryKey: ['getVerificationMessages'] });
  }, [queryClient]);

  const message = latestBySentAt(data?.messages ?? []);
  const recordedReply = latestBySentAt(data?.replies ?? []);

  // The later of the two, not the record by preference: replying a second time
  // must not be masked by the first reply already being on the record.
  const replyAt = laterOf(asDate(recordedReply?.sent_at), markedReplyAt);

  return {
    message,
    // Only a reply that came after the ask answers it. A second "needs more
    // info" email sent to an account that already replied is a fresh ask, and
    // showing it as answered would bury it.
    repliedAt: answersAsk(replyAt, asDate(message?.sent_at)) ? replyAt : null,
    // A disabled query reports isPending forever — gate it, or other states spin forever.
    isLoading: needsMoreInfo && accountId != null && isPending,
    markReplied,
  };
}

/**
 * Whether the customer has answered the ask that is currently outstanding.
 *
 * Ask-aware on purpose, and shared by every surface that reports the review:
 * the raw session mark alone is wrong twice over. It is empty after a reload
 * even when the record holds a reply, and it stays set when a second question
 * arrives — which would leave the banner silent and the rail claiming to be
 * verifying while the step renders an unanswered ask.
 */
export function useHasRepliedToVerification(): boolean {
  return useVerificationMessage().repliedAt != null;
}

export type VerificationPhase = 'verified' | 'unsubmitted' | 'loading' | 'ask-outstanding' | 'awaiting-review';

export const VERIFICATION_PHASE_LABELS = {
  unsubmitted: 'Verify your account',
  'ask-outstanding': 'Verify your account',
  'awaiting-review': 'Verifying your account',
} as const satisfies Partial<Record<VerificationPhase, string>>;

export function useVerificationPhase(): VerificationPhase {
  const isVerified = useIsAccountVerified();
  const needsAction = useVerificationNeedsAction();
  const { repliedAt, isLoading } = useVerificationMessage();
  const verificationState = useStore(accountStore)?.verification_state;
  if (isVerified) {
    return 'verified';
  }
  if (verificationState === VerificationState.Unsubmitted) {
    return 'unsubmitted';
  }
  if (!needsAction) {
    return 'awaiting-review';
  }
  if (isLoading) {
    return 'loading';
  }
  return repliedAt == null ? 'ask-outstanding' : 'awaiting-review';
}
